import type { CSSProperties } from 'react';

/**
 * How a product category looks in the buying list: a tone (one of the `--color-cat-N` token
 * sets) and a line icon. The categories are data, not an enum, so the ones the catalogue has
 * today are named here and anything else gets a stable tone from its name and a generic box.
 */
export interface CategoryStyle {
  readonly tone: number;
  readonly icon: string;
}

const TONES = 7;

const BOX = 'M4 8l8-4 8 4-8 4zM4 8v8l8 4 8-4V8M12 12v8';

const KNOWN: Readonly<Record<string, CategoryStyle>> = {
  mercearia: {
    tone: 1,
    icon: 'M8 3h8l-1 3H9zM9 6c-3 2-4 5-4 8 0 4 3 7 7 7s7-3 7-7c0-3-1-6-4-8M10 13h4',
  },
  limpeza: { tone: 2, icon: 'M9 3h5v3H9zM10 6h3M8 9h7l1 11H7zM14 3l3-1M16 6l2 1' },
  bebidas: {
    tone: 3,
    icon: 'M10 2h4v3l2 3v12a1 1 0 0 1-1 1H9a1 1 0 0 1-1-1V8l2-3zM8 12h8',
  },
  laticinios: { tone: 4, icon: 'M8 7l2-4h4l2 4v14H8zM8 7h8M11 12h2' },
  hortifruti: {
    tone: 5,
    icon: 'M12 8c-4-2-8 1-7 6 1 4 4 7 7 6 3 1 6-2 7-6 1-5-3-8-7-6zM12 8c0-2 1-4 3-5',
  },
  carnes: {
    tone: 6,
    icon: 'M15 4a5 5 0 0 1 4 8l-5 3-3 3a2 2 0 1 1-3-3l3-3 1-5a5 5 0 0 1 3-3zM7 17l-2 2',
  },
  descartaveis: {
    tone: 7,
    icon: 'M6 5h12l-1.5 15a1 1 0 0 1-1 1h-7a1 1 0 0 1-1-1zM5 5h14M7 10h10',
  },
};

function normalise(name: string): string {
  return name.normalize('NFD').replace(/[̀-ͯ]/gu, '').trim().toLowerCase();
}

export function categoryStyle(name: string | null | undefined): CategoryStyle {
  if (name == null || name.trim() === '') {
    return { tone: 7, icon: BOX };
  }
  const key = normalise(name);
  const known = KNOWN[key];
  if (known !== undefined) {
    return known;
  }
  let hash = 0;
  for (const char of key) {
    hash = (hash * 31 + char.charCodeAt(0)) >>> 0;
  }
  return { tone: (hash % TONES) + 1, icon: BOX };
}

/** The custom properties a category's elements read: `--cat`, `--cat-bg`, `--cat-line`. */
export function categoryVars(style: CategoryStyle): CSSProperties {
  return {
    '--cat': `var(--color-cat-${String(style.tone)})`,
    '--cat-bg': `var(--color-cat-${String(style.tone)}-bg)`,
    '--cat-line': `var(--color-cat-${String(style.tone)}-line)`,
  } as CSSProperties;
}
